/**
 * Progressive health-snapshot sections. Field sets match
 * backend/models/health_profile.py (EXTENDED_SECTIONS, SECTION_FIELDS,
 * NONE_REPORTABLE, LEGACY_SECTION_FIELDS).
 */

export const EXTENDED_SECTIONS = ["safety", "history", "lifestyle"];

export const NONE_REPORTABLE = [
  "medications",
  "allergies",
  "chronic_conditions",
  "surgeries_recent",
];

export const SECTION_FIELDS = {
  safety: ["medications", "allergies"],
  history: ["chronic_conditions", "surgeries_recent"],
  lifestyle: [
    "height_cm",
    "weight_kg",
    "smoking",
    "alcohol",
    "activity_level",
    "sleep_hours_typical",
  ],
};

export const LEGACY_SECTION_FIELDS = {
  history: ["chronic_conditions_other"],
  lifestyle: ["activity_days_per_week", "sleep_regular", "notes_other"],
};

export const LIST_FIELDS = ["medications", "allergies", "chronic_conditions"];

export const SMOKING_OPTIONS = ["never", "former", "current"];
export const ALCOHOL_OPTIONS = ["none", "occasional", "regular", "prefer_not_to_say"];
export const ACTIVITY_OPTIONS = ["sedentary", "light", "moderate", "vigorous"];
export const SLEEP_OPTIONS = ["regular", "irregular", "prefer_not_to_say"];

const SAFETY_DISMISS_PREFIX = "mc_safety_dismissed:";

const TEXT_KEY = {
  medications: "medicationsText",
  allergies: "allergiesText",
  chronic_conditions: "chronic_conditionsText",
  surgeries_recent: "surgeriesText",
};

export function splitList(text) {
  return String(text || "")
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function joinList(arr) {
  return Array.isArray(arr) ? arr.join(", ") : "";
}

function valuePresent(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.some((item) => String(item).trim());
  return true;
}

/**
 * Answered section keys for a subject list item.
 * An array (even empty) is authoritative. A missing array plus
 * tier_extended_complete treats all three sections as answered.
 */
export function effectiveSectionsAnswered(subject) {
  if (!subject || typeof subject !== "object") return new Set();
  if (Array.isArray(subject.sections_answered)) {
    return new Set(
      subject.sections_answered.filter((key) => EXTENDED_SECTIONS.includes(key)),
    );
  }
  if (subject.tier_extended_complete === true) {
    return new Set(EXTENDED_SECTIONS);
  }
  return new Set();
}

/** @returns {"answered" | "unanswered"} */
export function sectionStatus(subject, key) {
  return effectiveSectionsAnswered(subject).has(key) ? "answered" : "unanswered";
}

export function isSafetyAnswered(subject) {
  return sectionStatus(subject, "safety") === "answered";
}

export function showHealthContextRow(subject, caseOneOff) {
  if (caseOneOff) return false;
  if (!subject || subject.tier_a_complete !== true) return false;
  return true;
}

export function showSafetyQuickCard(subject, caseOneOff, dismissed) {
  if (caseOneOff || !subject?.subject_id) return false;
  if (dismissed) return false;
  return !isSafetyAnswered(subject);
}

export function safetyDismissStorageKey(subjectId) {
  if (typeof subjectId !== "string" || !subjectId.trim()) return null;
  return `${SAFETY_DISMISS_PREFIX}${subjectId}`;
}

export function isSafetyDismissed(subjectId) {
  const key = safetyDismissStorageKey(subjectId);
  if (!key || typeof localStorage === "undefined") return false;
  return localStorage.getItem(key) === "1";
}

export function dismissSafety(subjectId) {
  const key = safetyDismissStorageKey(subjectId);
  if (!key || typeof localStorage === "undefined") return;
  localStorage.setItem(key, "1");
}

export function safetyNonePayload() {
  return {
    section: "safety",
    none_reported: ["medications", "allergies"],
  };
}

export function sectionFormState(extended) {
  const source = extended && typeof extended === "object" ? extended : {};
  const none = new Set(
    Array.isArray(source.none_reported) ? source.none_reported : [],
  );
  const state = {
    medications: "",
    medicationsText: "",
    allergies: "",
    allergiesText: "",
    chronic_conditions: "",
    chronic_conditionsText: "",
    surgeries_recent: "",
    surgeriesText: "",
    height_cm: source.height_cm != null ? String(source.height_cm) : "",
    weight_kg: source.weight_kg != null ? String(source.weight_kg) : "",
    smoking: source.smoking || "",
    alcohol: source.alcohol || "",
    activity_level: source.activity_level || "",
    sleep_hours_typical:
      source.sleep_hours_typical != null ? String(source.sleep_hours_typical) : "",
    chronic_conditions_other: source.chronic_conditions_other || "",
    activity_days_per_week:
      source.activity_days_per_week != null
        ? String(source.activity_days_per_week)
        : "",
    sleep_regular: source.sleep_regular || "",
    notes_other: source.notes_other || "",
  };
  for (const field of NONE_REPORTABLE) {
    if (valuePresent(source[field])) state[field] = "yes";
    else if (none.has(field)) state[field] = "no";
    if (field === "surgeries_recent") {
      state[TEXT_KEY[field]] = typeof source[field] === "string" ? source[field] : "";
    } else {
      state[TEXT_KEY[field]] = joinList(source[field]);
    }
  }
  return state;
}

function assignNumber(payload, key, text) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return;
  const value = Number(trimmed);
  if (Number.isFinite(value)) payload[key] = value;
}

function assignTextList(payload, none, field, choice, text) {
  if (choice === "no") {
    none.push(field);
    return;
  }
  if (choice !== "yes") return;
  if (field === "surgeries_recent") {
    const trimmed = String(text || "").trim();
    if (trimmed) payload[field] = trimmed;
    return;
  }
  const items = splitList(text);
  if (items.length) payload[field] = items;
}

/**
 * Body for PUT .../profile/extended for a single section.
 * Saving marks the section even when every none-reportable answer is No.
 */
export function buildSectionSave(section, state) {
  if (!EXTENDED_SECTIONS.includes(section)) {
    throw new Error("Unknown health section");
  }
  const payload = { section };
  if (section === "lifestyle") {
    assignNumber(payload, "height_cm", state.height_cm);
    assignNumber(payload, "weight_kg", state.weight_kg);
    assignNumber(payload, "sleep_hours_typical", state.sleep_hours_typical);
    if (state.smoking) payload.smoking = state.smoking;
    if (state.alcohol) payload.alcohol = state.alcohol;
    if (state.activity_level) payload.activity_level = state.activity_level;
    return payload;
  }
  const none = [];
  for (const field of SECTION_FIELDS[section]) {
    assignTextList(payload, none, field, state[field], state[TEXT_KEY[field]]);
  }
  if (none.length) payload.none_reported = none;
  return payload;
}

export function sectionIsTouched(section, state) {
  if (section === "lifestyle") {
    return SECTION_FIELDS.lifestyle.some((key) => String(state[key] ?? "").trim());
  }
  return SECTION_FIELDS[section].some(
    (field) => state[field] === "yes" || state[field] === "no",
  );
}

/** Legacy full-form fields. Sent without `section` so the API keeps accepting them. */
export function buildLegacySave(state) {
  const payload = {};
  const other = String(state.chronic_conditions_other || "").trim();
  if (other) payload.chronic_conditions_other = other;
  assignNumber(payload, "activity_days_per_week", state.activity_days_per_week);
  if (state.sleep_regular) payload.sleep_regular = state.sleep_regular;
  const notes = String(state.notes_other || "").trim();
  if (notes) payload.notes_other = notes;
  return payload;
}

/**
 * Analytics status for a section save. "none" means the body carries no
 * field values (explicit No, or an empty lifestyle save).
 */
export function healthSectionSaveStatus(body) {
  if (!body || typeof body !== "object") return "answered";
  const valueKeys = Object.keys(body).filter(
    (key) => key !== "section" && key !== "none_reported",
  );
  return valueKeys.length === 0 ? "none" : "answered";
}

export function sectionChoicesComplete(section, state) {
  if (section === "lifestyle") return true;
  return SECTION_FIELDS[section].every(
    (field) => state[field] === "yes" || state[field] === "no",
  );
}
